import { Component } from "@theme/component";
import { isDesktopBreakpoint, isMobileBreakpoint, scrollHorizontalToElement } from "@theme/utilities";

class ScrollingHotspotsComponent extends Component {
  // Cached elements
  #scrollingHotspot = null;
  #hotspotsArray = null;
  #scrollContainer = null;
  #activeIndex = -1;
  #stickyTop = 0;
  #pendingScrollIndex = -1;
  // Scroll perf state
  #raf = 0;
  #isMobile = false;
  #isInView = true;
  #inViewObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#isMobile = isMobileBreakpoint();
    this.#cacheElements();
    this.#setupHotspots();
    this.#syncStickyTop();
    this.#syncScrollState();

    window.addEventListener("scroll", this.#handleScroll, { passive: true });
    window.addEventListener("resize", this.#handleResize, { passive: true });

    // Only run the (layout-reading) opacity pass while the section is on screen.
    if (window.IntersectionObserver) {
      this.#inViewObserver = new IntersectionObserver((entries) => {
        this.#isInView = !!entries[0]?.isIntersecting;
        if (this.#isInView) this.#scheduleOpacityUpdate();
      }, { rootMargin: "100% 0px" });
      this.#inViewObserver.observe(this);
    }

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', this.#changeHotspotSelected);
    }
  }

  /*
   * Changes the hotspot selected
   * @param {Event} event - The event object
   */
  #changeHotspotSelected = (event) => {
    const target = event.target;
    const hotspot = target?.classList?.contains("scrolling-hotspot")
      ? target
      : target?.closest(".scrolling-hotspot");
    const index = hotspot ? this.#scrollingHotspot.indexOf(hotspot) : -1;
    if (index >= 0) {
      this.#scrollToHotspot(index);
      this.#setActiveIndex(index);
      this.#syncScrollState();
    }
  };

  /**
   * Caches frequently accessed DOM elements
   */
  #cacheElements() {
    this.#scrollingHotspot = Array.from(this.querySelectorAll(".scrolling-hotspot"))
      .filter(el => el.querySelector(".scrolling-hotspot__item"));
    this.#scrollContainer = this.querySelector(".scrolling-hotspots__content");
  }

  #syncStickyTop() {
    if (isMobileBreakpoint()) {
      return;
    }
    const header_desktop = document.querySelector(".header__desktop ");
    if (!header_desktop) {
      return;
    }
    const header_desktop_type = header_desktop.dataset.stickyType;
    if(header_desktop_type !== "always") {
      return;
    }
    this.#stickyTop = parseFloat(header_desktop.offsetHeight) || 0;
    if(header_desktop.classList.contains("header-sticky--boxed")) {
      this.#stickyTop += 16;
    }
  }

  /**
   * Sets up hotspots for each product card
   * Clones template, applies CSS variables, and attaches event listeners
   */
  #setupHotspots() {
    let index = 0;
    this.#scrollingHotspot.forEach((scrollingHotspot) => {
      const hotspot = scrollingHotspot.querySelector(".scrolling-hotspot__item");
      if (!hotspot) return;
      if (index === 0 && isMobileBreakpoint()) {
        hotspot.classList.add('active');
        this.#activeIndex = 0;
      }
      hotspot.classList.remove('hidden');
      index++;
      this.#attachHotspotEvents(hotspot);
      this.querySelector(".scrolling-hotspots__image").appendChild(hotspot);
    });

    this.#hotspotsArray = Array.from(this.querySelectorAll(".scrolling-hotspot__item"));
  }


  /**
   * Attaches event listeners to hotspot
   * @param {HTMLElement} hotspotEl - The hotspot element
   */
  #attachHotspotEvents(hotspotEl) {
    hotspotEl.addEventListener("click", this.#handleHotspotClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Cleanup hotspot events
    if (this.#hotspotsArray) {
      this.#hotspotsArray.forEach(hotspot => {
        hotspot.removeEventListener("click", this.#handleHotspotClick);
      });
    }

    window.removeEventListener("scroll", this.#handleScroll);
    window.removeEventListener("resize", this.#handleResize);

    if (this.#raf) {
      cancelAnimationFrame(this.#raf);
      this.#raf = 0;
    }
    this.#inViewObserver?.disconnect();

    if (Shopify.designMode) {
      this.removeEventListener('shopify:block:select', this.#changeHotspotSelected);
    }
  }

  /**
   * Sets the active hotspot and removes active state from others
   * @param {HTMLElement} hotspotEl - The hotspot element to activate
   */
  #setActiveHotspot(hotspotEl) {
    if (!this.#hotspotsArray) {
      return;
    }
    this.#hotspotsArray.forEach((el) => el.classList.remove("active"));
    hotspotEl.classList.add("active");
  }

  #setActiveIndex(index) {
    if (!this.#scrollingHotspot?.length) return;
    if (index < 0 || index >= this.#scrollingHotspot.length) return;
    if (this.#activeIndex === index) return;
    this.#activeIndex = index;

    this.#scrollingHotspot.forEach((el, i) => el.classList.toggle("active", i === index));
    const hotspot = this.#hotspotsArray?.[index];
    if (hotspot) this.#setActiveHotspot(hotspot);
  }


  /**
   * Handles click event for hotspots
   * Works for both mouse clicks and touch taps
   */
  #handleHotspotClick = (event) => {
    const hotspot = event.currentTarget ?? event.target;
    if (!hotspot) return;

    this.#activateHotspotAndScroll(hotspot);
  };

  /**
   * Activates hotspot and scrolls to corresponding product
   * @param {HTMLElement} hotspotEl - The hotspot element
   */
  #activateHotspotAndScroll(hotspotEl) {
    this.#setActiveHotspot(hotspotEl);
    if (this.#hotspotsArray) {
      const index = this.#hotspotsArray.indexOf(hotspotEl);
      if (index >= 0) {
        this.#pendingScrollIndex = index;
        this.#setActiveIndex(index);
        this.#scrollToHotspot(index);
      }
    }
  }

  /**
   * Scrolls to the hotspot corresponding to the hotspot marker
   * @param {HTMLElement} index - The hotspot index
   */
  #scrollToHotspot(index) {
    const targetCard = this.#scrollingHotspot[index];
    if (!targetCard) return;

    if (isMobileBreakpoint()) {
      scrollHorizontalToElement(this.#scrollContainer, targetCard);
      return;
    }

    this.#scrollElementToViewportCenter(targetCard);
  }

  #scrollElementToViewportCenter(el) {
    const rect = el.getBoundingClientRect();
    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    const absoluteTop = rect.top + currentScrollY;

    const visibleCenterY = window.innerHeight / 2 + this.#stickyTop / 2;
    const desiredScrollTop = absoluteTop - (visibleCenterY - rect.height / 2);
    const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const clampedScrollTop = Math.min(maxScrollTop, Math.max(0, desiredScrollTop));

    window.scrollTo({
      top: clampedScrollTop,
      behavior: "smooth"
    });
  }

  #syncScrollState() {
    if (!this.#scrollingHotspot?.length) return;
    this.#updateOpacity();
  }

  /** Coalesce scroll events into one layout-reading pass per frame. */
  #scheduleOpacityUpdate = () => {
    if (!this.#isInView || this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#updateOpacity();
    });
  };

  #handleScroll = () => {
    this.#scheduleOpacityUpdate();
  };

  #handleResize = () => {
    this.#isMobile = isMobileBreakpoint();
    this.#syncStickyTop();
    this.#updateOpacity();
    this.#updateOpacityMobile();
  };

  #updateOpacity() {
    if (!this.#scrollingHotspot?.length) return;
    if (this.#isMobile) return;
    const center = window.innerHeight / 2 + this.#stickyTop / 2;
    const lastIndex = this.#scrollingHotspot.length - 1;
    let bestIndex = -1;
    let bestDistance = Infinity;

    // Read phase: gather all geometry before touching styles to avoid
    // interleaved read/write forced reflows.
    const updates = this.#scrollingHotspot.map((el, index) => {
      const rect = el.getBoundingClientRect();
      const elCenter = rect.top + rect.height / 2;
      const distance = Math.abs(center - elCenter);

      let opacity = 1 - distance / 500;
      const minOpacity = index === 0 ? 0.2 : 0;
      if (distance <= 100) opacity = 1;
      opacity = Math.max(minOpacity, Math.min(1, opacity));

      if (index === lastIndex && elCenter <= center) {
        opacity = 1;
      }

      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }

      return { el, opacity };
    });

    // Write phase: apply all style mutations together.
    for (const { el, opacity } of updates) {
      el.style.opacity = opacity.toFixed(4);
    }

    if (bestIndex >= 0 && bestDistance < 100) {
      if (this.#pendingScrollIndex === -1 || bestIndex === this.#pendingScrollIndex) {
        this.#pendingScrollIndex = -1;
        this.#setActiveIndex(bestIndex);
      }
    } else if (this.#pendingScrollIndex === -1) {
      this.#clearActiveHotspots();
    }
  }

  #clearActiveHotspots() {
    if (this.#activeIndex === -1) return;
    this.#activeIndex = -1;
    this.#scrollingHotspot?.forEach(el => el.classList.remove("active"));
    this.#hotspotsArray?.forEach(el => el.classList.remove("active"));
  }

  #updateOpacityMobile() {
    if (!this.#scrollingHotspot?.length) return;
    if (!this.#isMobile) return;
    this.#scrollingHotspot.forEach((el) => {
      el.style.opacity = 1;
    });
  }
}

if (!customElements.get("scrolling-hotspots")) {
  customElements.define("scrolling-hotspots", ScrollingHotspotsComponent);
}
